import { useState, useCallback, useEffect } from 'react';
import {
  ToggleLeft,
  SlidersHorizontal,
  Search,
  Beaker,
  ShieldAlert,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateCriterion, useUpdateCriterion } from '@/features/criteria/hooks/useCriteria';
import { toast } from 'sonner';
import type {
  Criterion,
  CriterionType,
  CriterionConfig,
  BooleanConfig,
  ScaleConfig,
  TextMatchConfig,
  PICOConfig,
  BiasRiskConfig,
} from '@/features/criteria/types';
import {
  DEFAULT_CONFIGS,
  CRITERION_TYPE_LABELS,
  CRITERION_TYPE_DESCRIPTIONS,
} from '@/features/criteria/types';

// =========================================
// Props
// =========================================

interface CriteriaBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  criterion?: Criterion | null;
  onSuccess?: (criterion: Criterion) => void;
}

// =========================================
// Type icon mapping
// =========================================

const TYPE_ICONS: Record<CriterionType, typeof ToggleLeft> = {
  boolean: ToggleLeft,
  scale: SlidersHorizontal,
  text_match: Search,
  pico: Beaker,
  bias_risk: ShieldAlert,
};

const TYPE_COLORS: Record<CriterionType, string> = {
  boolean: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  scale: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  text_match: 'bg-amber-50 text-amber-700 border-amber-200',
  pico: 'bg-blue-50 text-blue-700 border-blue-200',
  bias_risk: 'bg-rose-50 text-rose-700 border-rose-200',
};

const WEIGHT_LABELS: Record<number, string> = {
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Critical',
};

// =========================================
// Component
// =========================================

export function CriteriaBuilder({
  open,
  onOpenChange,
  criterion,
  onSuccess,
}: CriteriaBuilderProps) {
  const isEditing = !!criterion;
  const createCriterion = useCreateCriterion();
  const updateCriterion = useUpdateCriterion();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<CriterionType>('boolean');
  const [weight, setWeight] = useState(3);
  const [category, setCategory] = useState('');
  const [config, setConfig] = useState<CriterionConfig>(
    DEFAULT_CONFIGS.boolean
  );

  // Reset form when dialog opens/closes or criterion changes
  useEffect(() => {
    if (open) {
      if (criterion) {
        setName(criterion.name);
        setDescription(criterion.description ?? '');
        setType(criterion.type);
        setWeight(criterion.weight);
        setCategory(criterion.category ?? '');
        setConfig(criterion.config);
      } else {
        setName('');
        setDescription('');
        setType('boolean');
        setWeight(3);
        setCategory('');
        setConfig({ ...DEFAULT_CONFIGS.boolean });
      }
    }
  }, [open, criterion]);

  // When type changes, reset config to defaults
  const handleTypeChange = useCallback(
    (newType: CriterionType) => {
      setType(newType);
      if (!isEditing || newType !== criterion?.type) {
        setConfig({ ...DEFAULT_CONFIGS[newType] });
      }
    },
    [isEditing, criterion]
  );

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      toast.error('Please enter a criterion name');
      return;
    }

    try {
      let result: Criterion;

      if (isEditing && criterion) {
        result = await updateCriterion.mutateAsync({
          id: criterion.id,
          updates: {
            name: name.trim(),
            description: description.trim() || undefined,
            config,
            weight,
            category: category.trim() || undefined,
          },
        });
      } else {
        result = await createCriterion.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          type,
          config,
          weight,
          category: category.trim() || undefined,
        });
      }

      toast.success(isEditing ? 'Criterion updated' : 'Criterion created');
      onSuccess?.(result);
      onOpenChange(false);
    } catch {
      // toast handled in useCreateCriterion / useUpdateCriterion onError
    }
  }, [
    name,
    description,
    type,
    config,
    weight,
    category,
    isEditing,
    criterion,
    createCriterion,
    updateCriterion,
    onSuccess,
    onOpenChange,
  ]);

  const isPending = createCriterion.isPending || updateCriterion.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Criterion' : 'New Criterion'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modify the criterion configuration.'
              : 'Define how sources should be evaluated on this criterion.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="criterion-name">Name *</Label>
            <Input
              id="criterion-name"
              placeholder="e.g. Randomized controlled trial"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="criterion-desc">Description</Label>
            <textarea
              id="criterion-desc"
              placeholder="Optional description of what this criterion evaluates..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Type selector */}
          {!isEditing && (
            <div className="space-y-2">
              <Label>Type *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(Object.keys(CRITERION_TYPE_LABELS) as CriterionType[]).map(
                  (t) => {
                    const Icon = TYPE_ICONS[t];
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => handleTypeChange(t)}
                        className={cn(
                          'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                          type === t
                            ? TYPE_COLORS[t] + ' border-2'
                            : 'border-border'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {CRITERION_TYPE_LABELS[t]}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground line-clamp-2">
                          {CRITERION_TYPE_DESCRIPTIONS[t]}
                        </span>
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          )}

          {/* Type badge if editing */}
          {isEditing && (
            <div className="flex items-center gap-2">
              <Label>Type</Label>
              <Badge variant="outline" className={cn(TYPE_COLORS[type])}>
                {CRITERION_TYPE_LABELS[type]}
              </Badge>
            </div>
          )}

          {/* Type-specific config */}
          <div className="space-y-2">
            <Label>Configuration</Label>
            <div className="rounded-lg border p-4 space-y-4">
              {type === 'boolean' && (
                <BooleanConfigForm
                  config={config as BooleanConfig}
                  onChange={(c) => setConfig(c)}
                />
              )}
              {type === 'scale' && (
                <ScaleConfigForm
                  config={config as ScaleConfig}
                  onChange={(c) => setConfig(c)}
                />
              )}
              {type === 'text_match' && (
                <TextMatchConfigForm
                  config={config as TextMatchConfig}
                  onChange={(c) => setConfig(c)}
                />
              )}
              {type === 'pico' && (
                <PICOConfigForm
                  config={config as PICOConfig}
                  onChange={(c) => setConfig(c)}
                />
              )}
              {type === 'bias_risk' && (
                <BiasRiskConfigForm
                  config={config as BiasRiskConfig}
                  onChange={(c) => setConfig(c)}
                />
              )}
            </div>
          </div>

          {/* Weight */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Weight</Label>
              <Badge variant="secondary">{weight} — {WEIGHT_LABELS[weight]}</Badge>
            </div>
            <Slider
              value={[weight]}
              onValueChange={(v) => setWeight(v[0] ?? 3)}
              min={1}
              max={5}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Low impact</span>
              <span>Critical</span>
            </div>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="criterion-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Quality">Quality</SelectItem>
                <SelectItem value="Methodology">Methodology</SelectItem>
                <SelectItem value="Relevance">Relevance</SelectItem>
                <SelectItem value="Framework">Framework</SelectItem>
                <SelectItem value="Bias">Bias</SelectItem>
                <SelectItem value="Study Design">Study Design</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending || !name.trim()}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Create Criterion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================
// Boolean Config Form
// =========================================

interface BooleanConfigFormProps {
  config: BooleanConfig;
  onChange: (config: BooleanConfig) => void;
}

function BooleanConfigForm({ config, onChange }: BooleanConfigFormProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="bool-question">Question</Label>
      <Input
        id="bool-question"
        placeholder="e.g. Is this a randomized controlled trial?"
        value={config.question}
        onChange={(e) => onChange({ ...config, question: e.target.value })}
      />
      <p className="text-xs text-muted-foreground">
        The AI will answer this question with Yes or No for each source.
      </p>
    </div>
  );
}

// =========================================
// Scale Config Form
// =========================================

interface ScaleConfigFormProps {
  config: ScaleConfig;
  onChange: (config: ScaleConfig) => void;
}

function ScaleConfigForm({ config, onChange }: ScaleConfigFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label htmlFor="scale-min">Min</Label>
          <Input
            id="scale-min"
            type="number"
            value={config.min}
            onChange={(e) =>
              onChange({ ...config, min: parseInt(e.target.value) || 0 })
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="scale-max">Max</Label>
          <Input
            id="scale-max"
            type="number"
            value={config.max}
            onChange={(e) =>
              onChange({ ...config, max: parseInt(e.target.value) || 10 })
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="scale-step">Step</Label>
          <Input
            id="scale-step"
            type="number"
            value={config.step}
            onChange={(e) =>
              onChange({ ...config, step: parseInt(e.target.value) || 1 })
            }
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Labels</Label>
        <div className="space-y-2">
          {Object.entries(config.labels).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <Badge variant="outline" className="w-10 justify-center">
                {key}
              </Badge>
              <Input
                value={label}
                onChange={(e) =>
                  onChange({
                    ...config,
                    labels: { ...config.labels, [key]: e.target.value },
                  })
                }
                placeholder={`Label for ${key}`}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  const newLabels = { ...config.labels };
                  delete newLabels[key];
                  onChange({ ...config, labels: newLabels });
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const nextKey = String(
              Math.max(0, ...Object.keys(config.labels).map(Number)) + 1
            );
            onChange({
              ...config,
              labels: { ...config.labels, [nextKey]: '' },
            });
          }}
        >
          <Plus className="mr-1 h-3 w-3" />
          Add Label
        </Button>
      </div>
    </div>
  );
}

// =========================================
// Text Match Config Form
// =========================================

interface TextMatchConfigFormProps {
  config: TextMatchConfig;
  onChange: (config: TextMatchConfig) => void;
}

function TextMatchConfigForm({ config, onChange }: TextMatchConfigFormProps) {
  const [keywordInput, setKeywordInput] = useState('');

  const addKeyword = useCallback(() => {
    const kw = keywordInput.trim();
    if (kw && !config.keywords.includes(kw)) {
      onChange({ ...config, keywords: [...config.keywords, kw] });
      setKeywordInput('');
    }
  }, [keywordInput, config, onChange]);

  return (
    <div className="space-y-4">
      {/* Keywords */}
      <div className="space-y-2">
        <Label>Keywords</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Type a keyword..."
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addKeyword();
              }
            }}
            className="flex-1"
          />
          <Button variant="outline" size="sm" onClick={addKeyword}>
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </div>
        {config.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {config.keywords.map((kw) => (
              <Badge key={kw} variant="secondary" className="gap-1">
                {kw}
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...config,
                      keywords: config.keywords.filter((k) => k !== kw),
                    })
                  }
                  className="ml-0.5 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Mode */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Match Mode</Label>
          <p className="text-xs text-muted-foreground">
            {config.mode === 'any'
              ? 'Source matches if ANY keyword is found'
              : 'Source matches only if ALL keywords are found'}
          </p>
        </div>
        <Select
          value={config.mode}
          onValueChange={(v) =>
            onChange({ ...config, mode: v as 'any' | 'all' })
          }
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Case sensitive */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Case Sensitive</Label>
          <p className="text-xs text-muted-foreground">
            Match keywords with exact case
          </p>
        </div>
        <Switch
          checked={config.case_sensitive}
          onCheckedChange={(checked) =>
            onChange({ ...config, case_sensitive: checked })
          }
        />
      </div>
    </div>
  );
}

// =========================================
// PICO Config Form
// =========================================

interface PICOConfigFormProps {
  config: PICOConfig;
  onChange: (config: PICOConfig) => void;
}

function PICOConfigForm({ config, onChange }: PICOConfigFormProps) {
  const fields: { key: keyof Omit<PICOConfig, 'instructions'>; label: string; placeholder: string }[] = [
    { key: 'population', label: 'Population', placeholder: 'e.g. Adults aged 18-65 with Type 2 diabetes' },
    { key: 'intervention', label: 'Intervention', placeholder: 'e.g. Metformin monotherapy' },
    { key: 'comparator', label: 'Comparator', placeholder: 'e.g. Placebo or standard care' },
    { key: 'outcome', label: 'Outcome', placeholder: 'e.g. HbA1c reduction at 12 weeks' },
  ];

  return (
    <div className="space-y-3">
      {fields.map(({ key, label, placeholder }) => (
        <div key={key} className="space-y-1.5">
          <Label htmlFor={`pico-${key}`}>{label}</Label>
          <Input
            id={`pico-${key}`}
            placeholder={placeholder}
            value={config[key]}
            onChange={(e) => onChange({ ...config, [key]: e.target.value })}
          />
        </div>
      ))}
      <div className="space-y-1.5">
        <Label htmlFor="pico-instructions">Additional Instructions</Label>
        <textarea
          id="pico-instructions"
          placeholder="Optional instructions for the AI evaluator..."
          value={config.instructions}
          onChange={(e) => onChange({ ...config, instructions: e.target.value })}
          rows={2}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>
    </div>
  );
}

// =========================================
// Bias Risk Config Form
// =========================================

interface BiasRiskConfigFormProps {
  config: BiasRiskConfig;
  onChange: (config: BiasRiskConfig) => void;
}

function BiasRiskConfigForm({ config, onChange }: BiasRiskConfigFormProps) {
  const [itemInput, setItemInput] = useState('');

  const addChecklistItem = useCallback(() => {
    const item = itemInput.trim();
    if (item && !config.checklist.includes(item)) {
      onChange({ ...config, checklist: [...config.checklist, item] });
      setItemInput('');
    }
  }, [itemInput, config, onChange]);

  return (
    <div className="space-y-4">
      {/* Checklist items */}
      <div className="space-y-2">
        <Label>Checklist Items</Label>
        <div className="flex gap-2">
          <Input
            placeholder="e.g. Was the allocation sequence random?"
            value={itemInput}
            onChange={(e) => setItemInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addChecklistItem();
              }
            }}
            className="flex-1"
          />
          <Button variant="outline" size="sm" onClick={addChecklistItem}>
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </div>
        {config.checklist.length > 0 && (
          <div className="space-y-1">
            {config.checklist.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
              >
                <span className="text-muted-foreground text-xs font-mono w-5">
                  {idx + 1}.
                </span>
                <span className="flex-1">{item}</span>
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...config,
                      checklist: config.checklist.filter((_, i) => i !== idx),
                    })
                  }
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Risk scale levels */}
      <div className="space-y-2">
        <Label>Risk Scale Levels</Label>
        <div className="flex flex-wrap gap-1.5">
          {config.scale.map((level) => (
            <Badge key={level} variant="outline" className="gap-1">
              {level.replace('_', ' ')}
              {config.scale.length > 2 && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...config,
                      scale: config.scale.filter((s) => s !== level),
                    })
                  }
                  className="ml-0.5 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Each checklist item will be rated on this scale.
        </p>
      </div>
    </div>
  );
}
